import { useEffect, useRef } from 'react';

/**
 * Attach this hook to a <video> ref on mobile pages.
 *
 * Behaviour:
 *  - When the device rotates to landscape AND the video has a source,
 *    request fullscreen (Fullscreen API) or webkitEnterFullscreen (iOS).
 *  - When the device rotates back to portrait AND the video is currently
 *    fullscreen, exit fullscreen.
 *  - Only fires on "mobile" screens (window.innerWidth ≤ 1024 at the time
 *    of the orientation event) so desktop wide windows are ignored.
 *  - Uses the ScreenOrientation API where available, falls back to the
 *    deprecated window.onorientationchange for older iOS Safari.
 */
export function useOrientationFullscreen(
  videoRef: React.RefObject<HTMLVideoElement | null>,
) {
  // Track whether *we* triggered fullscreen so we can exit cleanly
  const weEnteredFS = useRef(false);

  useEffect(() => {
    const isMobileViewport = () =>
      Math.min(window.screen.width, window.screen.height) <= 1024;

    const isLandscape = () =>
      window.screen.orientation
        ? window.screen.orientation.type.startsWith('landscape')
        : Math.abs(window.orientation as number) === 90;

    const isFullscreen = () =>
      !!(
        document.fullscreenElement ||
        (document as { webkitFullscreenElement?: Element })
          .webkitFullscreenElement
      );

    const enterFullscreen = async (video: HTMLVideoElement) => {
      try {
        if (video.requestFullscreen) {
          await video.requestFullscreen();
          weEnteredFS.current = true;
        } else if (
          (video as HTMLVideoElement & { webkitEnterFullscreen?: () => void })
            .webkitEnterFullscreen
        ) {
          // iOS Safari: webkitEnterFullscreen is synchronous
          (
            video as HTMLVideoElement & { webkitEnterFullscreen: () => void }
          ).webkitEnterFullscreen();
          weEnteredFS.current = true;
        }
      } catch {
        // User gesture not trusted or API blocked — silently ignore
      }
    };

    const exitFullscreen = async () => {
      try {
        if (document.exitFullscreen && document.fullscreenElement) {
          await document.exitFullscreen();
        } else if (
          (document as { webkitExitFullscreen?: () => void })
            .webkitExitFullscreen &&
          (document as { webkitFullscreenElement?: Element })
            .webkitFullscreenElement
        ) {
          (
            document as { webkitExitFullscreen: () => void }
          ).webkitExitFullscreen();
        }
      } catch {
        // ignore
      }
    };

    const handleOrientationChange = () => {
      if (!isMobileViewport()) return;
      const video = videoRef.current;
      if (!video || !video.src) return;

      if (isLandscape()) {
        if (!isFullscreen()) {
          enterFullscreen(video);
        }
      } else {
        // Rotated back to portrait
        if (weEnteredFS.current && isFullscreen()) {
          weEnteredFS.current = false;
          exitFullscreen();
        }
      }
    };

    // ScreenOrientation API (modern browsers, Android Chrome)
    const orientation = window.screen.orientation as
      | (ScreenOrientation & { addEventListener: (e: string, h: () => void) => void; removeEventListener: (e: string, h: () => void) => void })
      | undefined;

    if (orientation?.addEventListener) {
      orientation.addEventListener('change', handleOrientationChange);
    } else {
      // Fallback: deprecated orientationchange on window (iOS Safari < 16)
      window.addEventListener('orientationchange', handleOrientationChange);
    }

    // Also reset our flag when the user manually exits fullscreen
    const handleFSChange = () => {
      if (!isFullscreen()) {
        weEnteredFS.current = false;
      }
    };
    document.addEventListener('fullscreenchange', handleFSChange);
    document.addEventListener('webkitfullscreenchange', handleFSChange);

    return () => {
      if (orientation?.removeEventListener) {
        orientation.removeEventListener('change', handleOrientationChange);
      } else {
        window.removeEventListener('orientationchange', handleOrientationChange);
      }
      document.removeEventListener('fullscreenchange', handleFSChange);
      document.removeEventListener('webkitfullscreenchange', handleFSChange);
    };
  }, [videoRef]);
}
